import { ErrorCode } from '@repo/shared/errorCodes'
import type { Composer } from 'vue-i18n'

export interface ApiError {
  code?: string
  error?: string
  value?: { code?: string; error?: string }
}

export function extractApiErrorCode<T>(error: T): string | undefined {
  const apiError = error as ApiError | undefined
  return apiError?.code ?? apiError?.value?.code
}

type I18n = Pick<Composer, 't'>

export function getApiErrorMessage<T>(i18n: I18n, error: T): string {
  const code = extractApiErrorCode(error)

  switch (code) {
    case ErrorCode.PRODUCT_NAME_EXISTS:
      return i18n.t('error_product_name_exists')
    case ErrorCode.PRODUCT_NOT_FOUND:
      return i18n.t('error_product_not_found')
    case ErrorCode.DISCOUNT_CODE_EXISTS:
      return i18n.t('error_discount_code_exists')
    case ErrorCode.DISCOUNT_NOT_FOUND:
      return i18n.t('error_discount_not_found')
    case ErrorCode.DISCOUNT_INVALID:
      return i18n.t('error_discount_invalid')
    case ErrorCode.DISCOUNT_EXPIRED:
      return i18n.t('error_discount_expired')
    case ErrorCode.ORDER_NOT_FOUND:
      return i18n.t('error_order_not_found')
    case ErrorCode.ORDER_ALREADY_FULFILLED:
      return i18n.t('error_order_already_fulfilled')
    case ErrorCode.VALIDATION_FAILED:
      return i18n.t('error_validation_failed')
    case ErrorCode.NOT_FOUND:
      return i18n.t('error_not_found')
    case ErrorCode.AUTH_UNAUTHORIZED:
      return i18n.t('error_unauthorized')
    case ErrorCode.AUTH_FORBIDDEN:
      return i18n.t('error_forbidden')
    default:
      return i18n.t('error_unknown')
  }
}

export function getOAuthErrorMessage(i18n: I18n, errorCode: string, provider?: string): string {
  const providerName = provider === 'twitch' ? 'Twitch' : provider === 'discord' ? 'Discord' : 'account'

  switch (errorCode) {
    case ErrorCode.OAUTH_ACCOUNT_NOT_LINKED:
      return i18n.t('error_oauth_account_not_linked', { provider: providerName })
    case ErrorCode.OAUTH_ACCOUNT_EMAIL_MISMATCH:
      return i18n.t('error_oauth_account_email_mismatch', { provider: providerName })
    case ErrorCode.OAUTH_ACCOUNT_ALREADY_LINKED:
      return i18n.t('error_oauth_account_already_linked', { provider: providerName })
    case ErrorCode.OAUTH_ACCESS_DENIED:
    case 'access_denied':
      return i18n.t('error_oauth_access_denied')
    case ErrorCode.OAUTH_INVALID_CODE:
    case 'invalid_code':
      return i18n.t('error_oauth_invalid_code')
    case ErrorCode.OAUTH_LINK_FAILED:
    case 'oauth_link_failed':
    case ErrorCode.OAUTH_UNABLE_TO_LINK_ACCOUNT:
      return i18n.t('error_oauth_link_failed', { provider: providerName })
    case 'provider_unavailable':
      return i18n.t('error_oauth_provider_unavailable', { provider: providerName })
    case 'state_mismatch':
    case 'state_not_found':
    case 'state_invalid':
      return i18n.t('error_oauth_sign_in_interrupted')
    default:
      return i18n.t('error_oauth_generic')
  }
}
